/**
 * FUE Inventory KPIs Component
 * Displays key performance indicators for FUE inventory in the Details page.
 * Fetches live data from the licence-inventory summary API endpoint.
 */

import React, { useMemo } from 'react';
import {
  Button,
  Illustration,
  Loader,
  Typography,
} from '@saviynt/design-system';

import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import { useGetFueInventoryKpiQuery } from '../../../../data-layer/licenseManagementApi';
import { KPICards } from '../../../Shared/KPICards';
import type { KPICardData } from '../../../Shared/KPICards/type';
import { KPI_LAYOUTS } from '../../../Shared/KPICards/type';

import './FUEInventoryKPIs.css';

/**
 * @description Displays key performance indicators for FUE inventory including
 * purchased FUE, allocated FUE, dormant users, downgrade candidates, and potential
 * savings. Fetches live data from the licence-inventory summary API and renders
 * loading, error/retry, or KPI card states accordingly.
 * @returns {React.ReactElement} KPI cards with FUE inventory metrics in vertical layout
 */
function FUEInventoryKPIs(): React.ReactElement {
  const localize = useInvertedLocalize();
  const cloudLicenseKey = useTypedSelector(
    (state) => state.aag.licenseManagement.cloudLicenseKey
  );
  const { data, isLoading, isFetching, isError, refetch } =
    useGetFueInventoryKpiQuery({
      vendor: 'sap',
      cloudLicenseKey,
    });
  const kpiCards: KPICardData[] = useMemo(() => {
    if (!data || Object.keys(data).length === 0) {
      return [];
    }

    // downgradeCandidates can be a plain number, an object with count, or undefined
    const downgradeCandidatesCount =
      typeof data.downgradeCandidates === 'number'
        ? data.downgradeCandidates
        : data.downgradeCandidates?.count ?? 0;

    // potentialSavings display: prefer displayCost from API, fallback to displayValue
    const savingsDisplay =
      data.potentialSavings?.displayCost ??
      data.potentialSavings?.displayValue ??
      '';

    return [
      {
        value: (data.purchasedUnit ?? 0).toLocaleString(),
        label: 'Purchased FUE',
      },
      {
        value: (data.allocatedUnit ?? 0).toLocaleString(),
        label: 'Allocated FUE',
      },
      {
        value: (data.dormantUsers.count ?? 0).toLocaleString(),
        label: `Dormant Users (${(
          data.dormantUsers.unit ?? 0
        ).toLocaleString()} FUE)`,
      },
      {
        value: String(downgradeCandidatesCount),
        label: 'Downgrade Candidates',
      },
      {
        value: `${data.potentialSavings?.savingsUnit ?? 0} FUE`,
        label: `Potential Savings (${savingsDisplay})`,
      },
    ];
  }, [data]);

  if (isLoading || isFetching) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader kind='dots' size='medium' format='inline' color='brand' />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='LicenseManagement-FueInventoryKpis-emptyState'>
        <Illustration kind='InfoGeneral' />
        <Typography
          kind='h2'
          color='--color-foreground-primary-medium'
          className='LicenseManagement-FueInventoryKpis-emptyStateTitle'>
          {localize('Failed to load snapshot data')}
        </Typography>
        <Button
          kind='outlined'
          size='small'
          onClick={() => {
            refetch().catch(() => {});
          }}>
          {localize('Retry')}
        </Button>
      </div>
    );
  }

  if (kpiCards.length === 0) {
    return (
      <div className='LicenseManagement-FueInventoryKpis-emptyState'>
        <Illustration kind='InfoGeneral' />
        <Typography
          kind='h2'
          color='--color-foreground-primary-medium'
          className='LicenseManagement-FueInventoryKpis-emptyStateTitle'>
          {localize('No data available')}
        </Typography>
      </div>
    );
  }

  return <KPICards cards={kpiCards} layout={KPI_LAYOUTS.VERTICAL} />;
}

export default FUEInventoryKPIs;
